/* Performance-Prüfungen: unsichtbare Brushes, Splitter, Brushes mit sehr vielen Flächen sowie viele Lichter, Monster und Texturen.
 * Die Schwellen sind Faustwerte. Ohne DOM. */
import type { Brush, CheckFix, CheckItem, Doc, Vec3 } from './types.ts';
import I18n from '../i18n.ts';
import { bbox, faceNormal } from './brush.ts';
import { analyze } from './complexity.ts';
import { dot } from './vec.ts';

const _ = I18n.t;

export const PERF = { tinySize: 2, manyFaces: 16, manyFaceBrushes: 10, lights: 400, monsters: 40, textures: 64, hintMap: 400, noclipSize: 16, noclipBrushes: 20 } as const;

export const SPECIAL = /^(sky|clip|null|skip|hint|solidhint|bevel|origin|aaatrigger|boundingbox|contentwater|noclip)$/i;
export const isSpecial = (b: Brush): boolean => b.faces.every((f) => SPECIAL.test(f.tex));
/** Taugt als Verdecker: undurchsichtig und sichtbar (kein Wasser, keine durchsichtige Textur, kein Sky/Clip) */
export const isOpaque = (b: Brush): boolean => b.faces.every((f) => !SPECIAL.test(f.tex) && f.tex.charAt(0) !== '!' && f.tex.charAt(0) !== '{');

export interface Planes { b: Brush; id: number; n: Vec3[]; d: number[]; min: Vec3; max: Vec3 }
export function planesOf(b: Brush, id: number): Planes {
  const n = b.faces.map((f) => faceNormal(b.verts, f.vi)), bb = bbox(b);
  return { b, id, n, d: b.faces.map((f, i) => dot(n[i], b.verts[f.vi[0]])), min: bb.min, max: bb.max };
}
const EPS = 0.01;
export const inside = (p: Planes, v: Vec3): boolean => { for (let i = 0; i < p.n.length; i++) if (dot(p.n[i], v) - p.d[i] > EPS) return false; return true; };
const contains = (outer: Planes, inner: Planes): boolean => {
  for (let k = 0; k < 3; k++) if (inner.min[k] < outer.min[k] - EPS || inner.max[k] > outer.max[k] + EPS) return false;
  return inner.b.verts.every((v) => inside(outer, v));
};

/** IDs der Welt-Brushes, die komplett in einem anderen undurchsichtigen Welt-Brush liegen (bei Deckungsgleichen trifft es nur den später definierten) */
export function hiddenBrushes(doc: Doc): string[] {
  const all: Planes[] = [];
  (doc.ents[0] ? doc.ents[0].brushes : []).forEach((b) => { if (b.faces.length >= 4) all.push(planesOf(b, all.length)); });
  const occ = all.filter((p) => isOpaque(p.b)), out: string[] = [];
  all.forEach((a) => {
    if (isSpecial(a.b) && !a.b.faces.every((f) => /^sky$/i.test(f.tex))) {
      // Clip-/Hint-Brushes in einem Brush sind ebenfalls überflüssig, werden hier aber nicht geprüft: sie haben Zweck im Compile
      return;
    }
    for (const o of occ) {
      if (o === a || !contains(o, a)) continue;
      if (contains(a, o) && o.id > a.id) continue;
      out.push(a.b.id); return;
    }
  });
  return out;
}

/** Kleinteile in func_detail (Stufe ≥ 1, ohne zhlt_noclip), deren Kanten alle höchstens noclipSize lang sind (Griffe, Leisten, Deko): Kandidaten für zhlt_noclip.
 *  Gemessen: 256 solche Teile kosten 1134 Clipnodes, mit zhlt_noclip 89 (docs/optimierung-vorgaben.md). Ob man sie berühren soll, entscheidet der Mensch, deshalb nur ein Hinweis. */
export function noclipCandidates(doc: Doc): string[] {
  const out: string[] = [];
  doc.ents.forEach((e, i) => {
    const lvl = parseInt(e.kv.zhlt_detaillevel || '', 10);
    if (i === 0 || e.kv.classname !== 'func_detail' || !(lvl >= 1) || e.kv.zhlt_noclip === '1') return;
    e.brushes.forEach((b) => {
      const x = bbox(b);
      if (Math.max(x.max[0] - x.min[0], x.max[1] - x.min[1], x.max[2] - x.min[2]) <= PERF.noclipSize) out.push(b.id);
    });
  });
  return out;
}

/** Winzige Brushes (alle Kantenlängen unter tinySize): Splitter, die BSP-Ebenen und Portale kosten */
export function tinyBrushes(doc: Doc): string[] {
  const out: string[] = [];
  doc.ents.forEach((e) => e.brushes.forEach((b) => {
    const x = bbox(b);
    if (Math.max(x.max[0] - x.min[0], x.max[1] - x.min[1], x.max[2] - x.min[2]) < PERF.tinySize) out.push(b.id);
  }));
  return out;
}

/** Brushes mit sehr vielen Flächen (Zylinder, Kuppeln): jede Fläche ist eine Ebene für BSP, Clipnodes und Lightmaps */
export function manyFaceBrushes(doc: Doc): string[] {
  const out: string[] = [];
  doc.ents.forEach((e) => e.brushes.forEach((b) => { if (b.faces.length > PERF.manyFaces) out.push(b.id); }));
  return out;
}

/** Brush-Entities, die sich nie bewegen: ihre Flächen werden vom Compiler nicht verworfen, auch wenn sie in der Welt stecken */
const STATIC_BRUSH_ENTS = /^(func_wall|func_illusionary)$/;

/** Flächen statischer Brush-Entities, die komplett in einem undurchsichtigen Welt-Brush liegen oder bündig auf ihm und in ihn hineinzeigen: je Brush-ID die Flächenindizes */
export function coveredFaces(doc: Doc): Map<string, number[]> {
  const occ: Planes[] = [];
  (doc.ents[0] ? doc.ents[0].brushes : []).forEach((b) => { if (b.faces.length >= 4 && isOpaque(b)) occ.push(planesOf(b, occ.length)); });
  const out = new Map<string, number[]>();
  if (!occ.length) return out;
  doc.ents.forEach((e, i) => {
    if (i === 0 || !STATIC_BRUSH_ENTS.test(e.kv.classname || '')) return;
    e.brushes.forEach((b) => {
      b.faces.forEach((f, fi) => {
        if (SPECIAL.test(f.tex)) return;
        const vs = f.vi.map((k) => b.verts[k]), n = faceNormal(b.verts, f.vi);
        const c: Vec3 = [0, 0, 0]; vs.forEach((v) => { c[0] += v[0] / vs.length; c[1] += v[1] / vs.length; c[2] += v[2] / vs.length; });
        const p: Vec3 = [c[0] + n[0] * 0.5, c[1] + n[1] * 0.5, c[2] + n[2] * 0.5];
        const mn: Vec3 = [Infinity, Infinity, Infinity], mx: Vec3 = [-Infinity, -Infinity, -Infinity];
        vs.forEach((v) => { for (let k = 0; k < 3; k++) { if (v[k] < mn[k]) mn[k] = v[k]; if (v[k] > mx[k]) mx[k] = v[k]; } });
        for (const o of occ) {
          let out3 = false;
          for (let k = 0; k < 3; k++) if (mn[k] < o.min[k] - EPS || mx[k] > o.max[k] + EPS) out3 = true;
          if (out3 || !vs.every((v) => inside(o, v)) || !inside(o, p)) continue;
          const l = out.get(b.id) || []; l.push(fi); out.set(b.id, l); return;
        }
      });
    });
  });
  return out;
}

/** Brushes mit extrem skalierten Texturen (Skalierung unter 0,1 oder über 10): riesige Lightmaps und Tiling-Fehler */
export function extremeScaleBrushes(doc: Doc): string[] {
  const out: string[] = [];
  doc.ents.forEach((e) => e.brushes.forEach((b) => {
    if (b.faces.some((f) => !SPECIAL.test(f.tex) && [f.xs, f.ys].some((x) => !(Math.abs(x) >= 0.1 && Math.abs(x) <= 10)))) out.push(b.id);
  }));
  return out;
}

export function performanceItems(doc: Doc, hidden?: string[]): CheckItem[] {
  const out: CheckItem[] = [];
  const add = (id: string, text: string, sel?: { b: string[]; e: string[] }, fix?: CheckFix) => out.push(sel && (sel.b.length || sel.e.length) ? (fix ? { level: 'warn', id, text, sel, fix } : { level: 'warn', id, text, sel }) : { level: 'warn', id, text });
  const hid = hidden || hiddenBrushes(doc);
  if (hid.length) add('perf.hidden', _('perf.hidden', { count: hid.length }), { b: hid, e: [] }, 'delete');
  const tiny = tinyBrushes(doc);
  if (tiny.length) add('perf.tiny', _('perf.tiny', { count: tiny.length, size: PERF.tinySize }), { b: tiny, e: [] }, 'delete');
  const nc = noclipCandidates(doc);
  if (nc.length >= PERF.noclipBrushes) out.push({ level: 'info', id: 'perf.noclip', text: _('perf.noclip', { count: nc.length, size: PERF.noclipSize }), sel: { b: nc, e: [] }, fix: 'noclip' });
  const many = manyFaceBrushes(doc);
  if (many.length >= PERF.manyFaceBrushes) add('perf.many_faces', _('perf.many_faces', { count: many.length, faces: PERF.manyFaces }), { b: many, e: [] });
  const cov = coveredFaces(doc);
  if (cov.size) { let n = 0; cov.forEach((l) => { n += l.length; }); add('perf.entity_faces', _('perf.entity_faces', { count: n, brushes: cov.size }), { b: [...cov.keys()], e: [] }, 'null'); }
  const scaled = extremeScaleBrushes(doc);
  if (scaled.length) add('perf.tex_scale', _('perf.tex_scale', { count: scaled.length }), { b: scaled, e: [] });
  const lights = doc.ents.filter((e) => /^light(_spot|_environment)?$/.test(e.kv.classname || ''));
  if (lights.length >= PERF.lights) add('perf.lights', _('perf.lights', { count: lights.length }), { b: [], e: lights.map((e) => e.id) });
  const mons = doc.ents.filter((e) => /^monster_/.test(e.kv.classname || '') && !e.brushes.length);
  if (mons.length >= PERF.monsters) add('perf.monsters', _('perf.monsters', { count: mons.length }), { b: [], e: mons.map((e) => e.id) });
  const tex = new Set<string>();
  doc.ents.forEach((e) => e.brushes.forEach((b) => b.faces.forEach((f) => { if (!SPECIAL.test(f.tex)) tex.add(f.tex.toUpperCase()); })));
  if (tex.size >= PERF.textures) out.push({ level: 'info', id: 'perf.textures', text: _('perf.textures', { count: tex.size }) });
  const st = analyze(doc).structural;
  if (st >= PERF.hintMap && !doc.ents.some((e) => e.brushes.some((b) => b.faces.some((f) => /^(solid)?hint$/i.test(f.tex))))) out.push({ level: 'info', id: 'perf.no_hint', text: _('perf.no_hint', { count: st }) });
  return out;
}
